import { PayrollParseResult, PayrollParseIssue, PayrollItem, PayrollEmployeeRecord, PayrollDepartment, PayrollTotals, PayrollRun } from '../payrollTypes';

const rubricRegexSingle =
    /^\s*(\d{1,4})\s+([A-Z0-9./ªºÁÉÍÓÚÂÊÔÃÕÇ\-\s]+?)\s+([0-9][0-9:.,]*)\s+([0-9][0-9.,]*)\s+([PD])\s*$/i;
const rubricRegexDouble =
    /^\s*(\d{1,4})\s+([A-Z0-9./ªºÁÉÍÓÚÂÊÔÃÕÇ\-\s]+?)\s+([0-9][0-9:.,]*)\s+([0-9][0-9.,]*)\s+(P)\s+(\d{1,4})\s+([A-Z0-9./ªºÁÉÍÓÚÂÊÔÃÕÇ\-\s]+?)\s+([0-9][0-9:.,]*)\s+([0-9][0-9.,]*)\s+(D)\s*$/i;

const employeeLineRegex =
    /(Empr\.?|Contr\.?):\s*(\S+)\s+(.+?)\s+Situação:\s+(.+?)(?:\s+CPF:\s+([\d.\-]+))?\s+Adm:\s+([\d/]+)/;
const employeeLineTwoRegex =
    /Vínculo:\s+(.+?)\s+CC:\s+(\S+)\s+Depto:\s+(\S+)\s+Horas Mês:\s*([\d:,]*)/;
const employeeLineThreeRegex =
    /Cargo:\s+(.+?)\s+C\.B\.O:\s+(\S+)\s+Filial:\s+(\S+)\s+Salário:\s+([\d.,]+)/;
const departmentRegex = /Departamento:\s+(\d+)\s*[-–]\s*(.+)/;
const headerRegex = {
    company: /Empresa:\s+(.+)/,
    cnpj: /CNPJ:\s+([0-9./-]+)/,
    calculation: /Cálculo:\s+(.+)/,
    competence: /Competência:\s+([0-9/]+)/,
    emission: /Emissão:\s+([0-9/]{10})/,
    hour: /Horas:\s+([0-9:]+)/,
    pages: /Página:\s+(\d+)\/(\d+)/,
};

function currencyToNumber(value: string | undefined): number | undefined {
    if (!value) return undefined;
    const normalized = value.replace(/\./g, '').replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isNaN(parsed) ? undefined : parsed;
}

function hoursToNumber(value: string | undefined): number | undefined {
    if (!value) return undefined;
    const normalized = value.replace(',', '.').replace(':', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isNaN(parsed) ? undefined : parsed;
}

function cleanCpf(value: string | undefined): string | undefined {
    if (!value) return undefined;
    return value.replace(/\D/g, '');
}

function classifyNature(code: string, natureFlag: string): PayrollItem['nature'] {
    if (code === '843' || code === '856' || code === '826') return 'employer_charge';
    if (['998', '812', '826', '989', '942', '828', '999'].includes(code)) return 'tax';
    // Informative only codes
    if (
        [
            '805', '806', '808', '809', '811', '815', '816', '818', '819', '821', '836', '8112', '8126', '8169',
            '8190', '8198', '8417', '854', '8697', '8550', '8556', '931', '932', '937', '9591', '9596', '9597',
            '990', '991', '9179',
        ].includes(code)
    ) {
        return 'informative';
    }
    if (natureFlag === 'D') return 'deduction';
    return 'earning';
}

function parseRubricsFromLine(line: string, issues: PayrollParseIssue[]): PayrollItem[] {
    const normalized = line.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    const items: PayrollItem[] = [];
    const sanitizeDescription = (desc: string) => {
        const cleaned = desc.trim();
        const nestedMatch = cleaned.match(/^(P|D)\s+\d{1,4}\s+(.+)/);
        if (nestedMatch) {
            return nestedMatch[2].trim();
        }
        return cleaned;
    };

    const doubleMatch = normalized.match(rubricRegexDouble);
    if (doubleMatch) {
        const [, code1, desc1, ref1, amount1, , code2, desc2, ref2, amount2] = doubleMatch;
        const val1 = currencyToNumber(amount1);
        const val2 = currencyToNumber(amount2);
        if (val1 !== undefined) {
            const nature1 = classifyNature(code1, 'P');
            items.push({
                code: code1,
                description: sanitizeDescription(desc1),
                reference: ref1,
                amount: val1,
                nature: nature1,
                affectsNet: nature1 !== 'informative' && nature1 !== 'employer_charge',
                pdType: 'earning',
            });
        } else {
            issues.push({ message: 'Falha ao converter valor da rubrica P', line });
        }
        if (val2 !== undefined) {
            const nature2 = classifyNature(code2, 'D');
            items.push({
                code: code2,
                description: sanitizeDescription(desc2),
                reference: ref2,
                amount: val2,
                nature: nature2,
                affectsNet: nature2 !== 'informative' && nature2 !== 'employer_charge',
                pdType: 'deduction',
            });
        } else {
            issues.push({ message: 'Falha ao converter valor da rubrica D', line });
        }
        return items;
    }

    const singleMatch = normalized.match(rubricRegexSingle);
    if (singleMatch) {
        const [, code, description, reference, amount, pdFlag] = singleMatch;
        const parsedDescription = sanitizeDescription(description);
        const numericAmount = currencyToNumber(amount);
        if (numericAmount === undefined) {
            issues.push({ message: 'Falha ao converter valor da rubrica', line });
        } else {
            const nature = classifyNature(code, pdFlag);
            items.push({
                code,
                description: parsedDescription,
                reference: reference?.trim(),
                amount: numericAmount,
                nature,
                affectsNet: nature !== 'informative' && nature !== 'employer_charge',
                pdType: pdFlag === 'P' ? 'earning' : pdFlag === 'D' ? 'deduction' : undefined,
            });
        }
    }
    return items;
}

function parseTotals(rawLine: string): PayrollTotals | null {
    const line = rawLine.replace(/\s+/g, ' ');

    const extractNumber = (regexes: RegExp[]): number | undefined => {
        for (const rx of regexes) {
            const match = line.match(rx);
            if (match) return currencyToNumber(match[1]);
        }
        return undefined;
    };

    const extractInt = (regexes: RegExp[]): number | undefined => {
        for (const rx of regexes) {
            const match = line.match(rx);
            if (match) {
                const cleaned = match[1].replace(/[.,]/g, '');
                const parsed = Number.parseInt(cleaned, 10);
                if (!Number.isNaN(parsed)) return parsed;
            }
        }
        return undefined;
    };

    const earnings = extractNumber([/Proventos:\s*([\d.,]+)/i]);
    const nd = extractInt([/ND:\s*([\d.,]+)/i]);
    const deductions = extractNumber([/Descontos?:\s*([\d.,]+)/i]);
    const informative = extractNumber([
        /Informativ[ao]s?:\s*([\d.,]+)/i,
        /Informativa:\s*([\d.,]+)/i,
    ]);
    let informativeDeduction = extractNumber([
        /Informativa\s+Dedut[ou]ra?:\s*([\d.,]+)/i,
        /Informativa\s+Dedut[ií]vel:\s*([\d.,]+)/i,
    ]);
    let netPay = extractNumber([
        /L[ií]quido(?:\s+a\s+receber)?:\s*([\d.,]+)/i,
        /Liq\.?:\s*([\d.,]+)/i,
    ]);
    const baseInss = extractNumber([/Base\s+INSS:\s*([\d.,]+)/i]);
    const excedenteInss = extractNumber([/Excedente\s+INSS:\s*([\d.,]+)/i]);
    let baseFgts = extractNumber([/Base\s+FGTS:\s*([\d.,]+)/i]);
    const fgtsValue = extractNumber([/Valor\s+FGTS:\s*([\d.,]+)/i]);
    let baseIrrf = extractNumber([/Base\s+IRRF:\s*([\d.,]+)/i]);

    if (!netPay) {
        const dedutoraNetMatch = line.match(/Informativa\s+Dedut[ou]ra?:\s*([\d.,]+)\s+([\d.,]+)/i);
        if (dedutoraNetMatch) {
            if (!informativeDeduction) {
                informativeDeduction = currencyToNumber(dedutoraNetMatch[1]);
            }
            netPay = currencyToNumber(dedutoraNetMatch[2]);
        }
    }

    const fgtsIrrfMatch = line.match(/Base\s+FGTS:\s*([\d.,]*)\s*Base\s+IRRF:\s*([\d.,]*)(?:\s+([\d.,]+))?/i);
    if (fgtsIrrfMatch) {
        const fgtsCandidate = currencyToNumber(fgtsIrrfMatch[1]);
        const irrfCandidate = currencyToNumber(fgtsIrrfMatch[2]);
        const trailingNumber = currencyToNumber(fgtsIrrfMatch[3]);

        if (fgtsCandidate === undefined && irrfCandidate !== undefined && trailingNumber !== undefined) {
            if (!baseFgts) baseFgts = irrfCandidate;
            if (!baseIrrf) baseIrrf = trailingNumber;
        } else {
            if (!baseFgts && fgtsCandidate !== undefined) baseFgts = fgtsCandidate;
            if (!baseIrrf && irrfCandidate !== undefined) baseIrrf = irrfCandidate;
            if (!baseIrrf && trailingNumber !== undefined) {
                baseIrrf = trailingNumber;
            }
            if (!baseFgts && fgtsCandidate === undefined && trailingNumber !== undefined) {
                baseFgts = trailingNumber;
            }
        }
    }

    const hasData =
        nd !== undefined ||
        earnings !== undefined ||
        deductions !== undefined ||
        informative !== undefined ||
        informativeDeduction !== undefined ||
        netPay !== undefined ||
        baseInss !== undefined ||
        excedenteInss !== undefined ||
        baseFgts !== undefined ||
        fgtsValue !== undefined ||
        baseIrrf !== undefined;

    if (!hasData) return null;

    return {
        nd,
        earnings,
        deductions,
        informative,
        informativeDeduction,
        netPay,
        baseInss,
        excedenteInss,
        baseFgts,
        fgtsValue,
        baseIrrf,
    };
}

function parseHeader(lines: string[]): PayrollRun {
    const run: PayrollRun = {};
    for (const ln of lines.slice(0, 40)) {
        if (!run.companyName) {
            const m = ln.match(headerRegex.company);
            if (m) run.companyName = m[1].trim();
        }
        if (!run.cnpj) {
            const m = ln.match(headerRegex.cnpj);
            if (m) run.cnpj = m[1].trim();
        }
        if (!run.calculationType) {
            const m = ln.match(headerRegex.calculation);
            if (m) run.calculationType = m[1].trim();
        }
        if (!run.competence) {
            const m = ln.match(headerRegex.competence);
            if (m) {
                const raw = m[1].trim();
                const [mm, yyyy] = raw.includes('/') ? raw.split('/') : [undefined, undefined];
                run.competence = yyyy && mm ? `${yyyy}-${mm}` : raw;
            }
        }
        if (!run.emissionAt) {
            const mDate = ln.match(headerRegex.emission);
            const mHour = ln.match(headerRegex.hour);
            if (mDate) {
                const date = mDate[1].split('/').reverse().join('-');
                run.emissionAt = mHour ? `${date}T${mHour[1]}` : date;
            }
        }
        if (!run.pageCount) {
            const m = ln.match(headerRegex.pages);
            if (m) run.pageCount = Number.parseInt(m[2], 10);
        }
    }
    return run;
}

export function parseJopelPayrollText(pages: string[]): PayrollParseResult {
    const issues: PayrollParseIssue[] = [];
    const allLines = pages.join('\n').split(/\r?\n/).map((ln) => ln.trim()).filter(Boolean);

    const run = parseHeader(allLines);
    const employees: PayrollEmployeeRecord[] = [];
    const departments: PayrollDepartment[] = [];

    let currentDept: PayrollDepartment | null = null;
    let currentEmployee: PayrollEmployeeRecord | null = null;
    let pendingTotalsLine: string | null = null;
    let lastTotalsCandidate: string | null = null;

    const mergeTotals = (existing: PayrollTotals | undefined, update: PayrollTotals | null): PayrollTotals | undefined => {
        if (!update) return existing;
        const result: PayrollTotals = { ...(existing ?? {}) };
        Object.entries(update).forEach(([key, value]) => {
            if (value !== undefined) {
                // @ts-expect-error dynamic assign
                result[key] = value;
            }
        });
        return result;
    };

    const pushEmployee = () => {
        if (currentEmployee) {
            if (!currentEmployee.totals || Object.keys(currentEmployee.totals).length === 0) {
                if (lastTotalsCandidate) {
                    issues.push({
                        message: `Totais não parseados para ${currentEmployee.name ?? currentEmployee.employeeId}: "${lastTotalsCandidate}"`,
                    });
                } else {
                    issues.push({
                        message: `Totais não encontrados para ${currentEmployee.name ?? currentEmployee.employeeId}`,
                    });
                }
            }
            employees.push(currentEmployee);
        }
        currentEmployee = null;
        pendingTotalsLine = null;
        lastTotalsCandidate = null;
    };

    for (const line of allLines) {
        const deptMatch = line.match(departmentRegex);
        if (deptMatch) {
            currentDept = { code: deptMatch[1], name: deptMatch[2].trim() };
            departments.push(currentDept);
            pendingTotalsLine = null;
            lastTotalsCandidate = null;
            continue;
        }

        const employeeMatch = line.match(employeeLineRegex);
        if (employeeMatch) {
            pushEmployee();
            pendingTotalsLine = null;
            lastTotalsCandidate = null;
            const [, , employeeId, name, status, cpf, admissionDate] = employeeMatch;
            currentEmployee = {
                employeeId,
                name: name.trim(),
                cpf: cleanCpf(cpf),
                status: status.trim(),
                admissionDate: admissionDate.trim(),
                employmentType: undefined,
                costCenter: undefined,
                departmentCode: currentDept?.code,
                branch: undefined,
                monthlyHours: undefined,
                position: undefined,
                cbo: undefined,
                baseSalary: undefined,
                department: currentDept ?? undefined,
                items: [],
                totals: {},
            };
            continue;
        }

        if (currentEmployee) {
            const candidateTotals = pendingTotalsLine ? `${pendingTotalsLine} ${line}` : line;
            if (/ND:|Proventos:|Descontos:|Informativa|L[ií]quido|Base INSS|Base FGTS/i.test(candidateTotals)) {
                const totals = parseTotals(candidateTotals);
                if (totals) {
                    currentEmployee.totals = mergeTotals(currentEmployee.totals, totals);
                    pendingTotalsLine = null;
                    lastTotalsCandidate = null;
                    continue;
                }
                pendingTotalsLine = candidateTotals;
                lastTotalsCandidate = candidateTotals;
                continue;
            } else {
                pendingTotalsLine = null;
                lastTotalsCandidate = null;
            }

            const lineTwoMatch = line.match(employeeLineTwoRegex);
            if (lineTwoMatch) {
                currentEmployee.employmentType = lineTwoMatch[1].trim();
                currentEmployee.costCenter = lineTwoMatch[2];
                currentEmployee.departmentCode = lineTwoMatch[3];
                currentEmployee.monthlyHours = hoursToNumber(lineTwoMatch[4]);
                continue;
            }

            const lineThreeMatch = line.match(employeeLineThreeRegex);
            if (lineThreeMatch) {
                currentEmployee.position = lineThreeMatch[1].trim();
                currentEmployee.cbo = lineThreeMatch[2];
                currentEmployee.branch = lineThreeMatch[3];
                currentEmployee.baseSalary = currencyToNumber(lineThreeMatch[4]) ?? undefined;
                continue;
            }

            const totals = parseTotals(line);
            if (totals) {
                currentEmployee.totals = mergeTotals(currentEmployee.totals, totals);
                continue;
            }

            const rubrics = parseRubricsFromLine(line, issues);
            if (rubrics.length > 0) {
                currentEmployee.items.push(...rubrics);
            }
        }
    }

    if (currentEmployee && (!currentEmployee.totals || Object.keys(currentEmployee.totals).length === 0)) {
        if (lastTotalsCandidate) {
            issues.push({
                message: `Totais não parseados para ${currentEmployee.name ?? currentEmployee.employeeId}: "${lastTotalsCandidate}"`,
            });
        } else {
            issues.push({
                message: `Totais não encontrados para ${currentEmployee.name ?? currentEmployee.employeeId}`,
            });
        }
    }

    pushEmployee();

    return {
        run: run.companyName || employees.length > 0 ? run : null,
        departments,
        employees,
        issues,
        rawText: pages,
    };
}
